import { useEffect, useRef, type RefObject } from 'react';
import type L from 'leaflet';
import { useAuthStore } from '../../store/authStore';
import { useMapOverlaysStore } from '../../store/mapOverlaysStore';
import { usePlanMapPlacementStore } from '../../store/planMapPlacementStore';
import { useUIStore } from '../../store/uiStore';
import { createRotatedImageOverlay, type RotatedImageOverlayLayer } from './rotatedImageOverlay';

// Dedicated pane. Above the tile layer (200) so an overlay always reads over
// the basemap; below every hazard/plan pane so a plan map — a moderator's
// scanned reference image — never competes with live data drawn on top of
// it: fhszPane (350), fireHistoryPane (360), the defense plan panes
// (450/460, raised further to 650/660 while a plan has focus — see
// PLAN_FOCUS_CSS in leafletHelpers.ts). A drawn defense or a hazard fill
// must always be able to sit over a reference map, never under one. The one
// thing that goes above all of them is PLAN_MAP_HANDLES_PANE (680) — see below.
export const PLAN_MAPS_PANE = 'planMapsPane';
const PLAN_MAPS_PANE_Z_INDEX = '330';

// The moderator's corner handles do NOT share the image's pane, because 330
// puts them under Leaflet's own marker pane (600) — and an observation
// marker or a cluster bubble is interactive, so it eats the mousedown meant
// for the handle underneath. On a map as busy as Big Bear (867 observations)
// that leaves most corners impossible to grab: measured Sept 18 2026 on dev
// with elementFromPoint, two of the three handles returned `obs-halo` /
// `obs-core` from the marker pane (Glenn's report). 680 clears every pane
// the app draws on the map — marker 600, tooltip 650, a focused defense plan
// 650/660 — and still sits under Leaflet's popup pane (700). Placement
// chrome is the moderator's controls; nothing on the map should cover them.
export const PLAN_MAP_HANDLES_PANE = 'planMapHandlesPane';
const PLAN_MAP_HANDLES_PANE_Z_INDEX = '680';

export function ensurePlanMapsPane(map: L.Map): void {
  if (!map.getPane(PLAN_MAPS_PANE)) {
    map.createPane(PLAN_MAPS_PANE).style.zIndex = PLAN_MAPS_PANE_Z_INDEX;
  }
}

export function ensurePlanMapHandlesPane(map: L.Map): void {
  if (!map.getPane(PLAN_MAP_HANDLES_PANE)) {
    map.createPane(PLAN_MAP_HANDLES_PANE).style.zIndex = PLAN_MAP_HANDLES_PANE_Z_INDEX;
  }
}

/**
 * Renders plan-map overlays (mapOverlays/{id} — moderator-imported wildfire
 * plan pages, placed by three corners) as `rotatedImageOverlay` layers: one
 * per overlay whose id is in `uiStore.enabledOverlayIds` (the Layers sheet
 * toggle) OR is the one currently in the placement editor
 * (`uiStore.placingOverlayId`) — an overlay being placed shows live even
 * before it's toggled on or shared.
 *
 * Owns the overlays subscription too (mapOverlaysStore.subscribe), and the
 * subscription is global: every overlay the reader may see, not just those
 * in one service area. It's keyed only on the moderator claim, so a
 * sign-in/out that flips `isModerator` re-subscribes to pick up (or drop)
 * drafts. It used to be keyed on whichever service area the map was
 * centered on (a point-in-polygon lookup on every `moveend`), which meant a
 * pan across the area's bbox re-keyed the listener, emptied `overlays`, and
 * dropped a toggled-on layer — and blanked the placement editor whenever
 * the doc being placed had a different `areaId` than the area under the map
 * center (PlanMapImportSheet.web.tsx always writes Big Bear). `areaId`
 * stays on the doc regardless, so a future Layers-sheet scope by viewport
 * (`serviceAreasStore.findIntersecting`) can build on it without a data
 * change.
 *
 * The overlay being placed is a special case for corners/opacity, not a
 * special case for rendering: while `placingOverlayId` matches, this hook
 * draws it from `planMapPlacementStore`'s uncommitted working state instead
 * of the Firestore doc, and flips it interactive (draggable/hit-testable)
 * — see usePlanMapPlacementLayer.web.ts, which drives that store and adds
 * the corner-handle markers on top of the same layer this hook owns. That
 * split (one hook renders the image, the other only the drag gesture) is
 * what keeps a placed overlay from ever drawing twice.
 */
export function useMapOverlaysLayer({
  mapRef,
  isLoaded,
}: {
  mapRef: RefObject<L.Map | null>;
  isLoaded: boolean;
}): void {
  const isModerator = useAuthStore((s) => s.isModerator);
  const overlays = useMapOverlaysStore((s) => s.overlays);
  const enabledOverlayIds = useUIStore((s) => s.enabledOverlayIds);
  const placingOverlayId = useUIStore((s) => s.placingOverlayId);
  const placementCorners = usePlanMapPlacementStore((s) => s.corners);
  const placementOpacity = usePlanMapPlacementStore((s) => s.opacity);

  useEffect(() => {
    const unsubscribe = useMapOverlaysStore.getState().subscribe({ moderator: isModerator });
    return () => unsubscribe();
  }, [isModerator]);

  const layersRef = useRef<Map<string, RotatedImageOverlayLayer>>(new Map());

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded) return;
    ensurePlanMapsPane(map);
    const layers = layersRef.current;
    const wanted = new Set(
      overlays
        .filter((o) => enabledOverlayIds.includes(o.id) || o.id === placingOverlayId)
        .map((o) => o.id)
    );

    for (const overlay of overlays) {
      if (!wanted.has(overlay.id)) continue;
      const isPlacing = overlay.id === placingOverlayId;
      // Working corners aren't ready on the very first render of a
      // placement (planMapPlacementStore.initialize hasn't run yet) — fall
      // back to the doc's own corners for that one frame rather than
      // skipping the layer, so it never flashes empty.
      const corners = isPlacing && placementCorners ? placementCorners : overlay.corners;
      const opacity = isPlacing ? placementOpacity : overlay.opacity;

      const existing = layers.get(overlay.id);
      if (existing) {
        existing.setCorners(corners);
        existing.setOpacity(opacity);
        existing.setInteractive(isPlacing);
        continue;
      }
      const layer = createRotatedImageOverlay(
        overlay.imageUrl,
        overlay.imageWidth,
        overlay.imageHeight,
        corners,
        {
          pane: PLAN_MAPS_PANE,
          opacity,
          interactive: isPlacing,
        }
      );
      layer.addTo(map);
      layer.getElement()?.setAttribute('data-overlay-id', overlay.id);
      layers.set(overlay.id, layer);
    }

    for (const [id, layer] of layers) {
      if (!wanted.has(id)) {
        layer.remove();
        layers.delete(id);
      }
    }
  }, [
    overlays,
    enabledOverlayIds,
    placingOverlayId,
    placementCorners,
    placementOpacity,
    isLoaded,
    mapRef,
  ]);

  // Full teardown on unmount only — the diff effect above already removes a
  // layer the moment it drops out of `wanted`.
  useEffect(() => {
    const layers = layersRef.current;
    return () => {
      for (const layer of layers.values()) layer.remove();
      layers.clear();
    };
  }, []);
}
